import { useMemo } from 'react';

import { getRegistrationDisplayPrice } from 'src/helpers/registrationPrice';
import {
  getParticipantEntryCompetitionDistanceIds,
  isParticipantCheckedIn,
  matchesCheckedInFilter,
  matchesCompetitionDistanceFilters,
  matchesParticipantQuery,
  matchesRegistrationDateRange,
  registrationCreatedAtMs,
  sortParticipants,
} from 'src/helpers/participantList';
import { ICheckin } from 'src/interfaces/Checkin';
import { IEnrichedParticipant, IParticipant } from 'src/interfaces/Participant';
import { IRegistration } from 'src/interfaces/Registration';
import { EOrder } from 'src/interfaces/Order';
import { IEdition } from 'src/interfaces/Edition';

import { RegistrationEnrichment } from './useMyRegistrationsMaps';

interface UseUserParticipantEnrichmentOptions {
  participants: IParticipant[];
  enrichmentByParticipantId: Record<string, RegistrationEnrichment>;
  registrationIdsByParticipantId: Record<string, string>;
  effectiveEditionId: string | null | undefined;
  editionsById: Record<string, IEdition> | undefined;
  checkinsById: Record<string, ICheckin> | undefined;
  checkinsByParticipantId: Record<string, ICheckin>;
  query: string;
  competitionFilter: string[];
  distanceFilter: string[];
  checkedInFilter: string[];
  registeredFrom: string | null;
  registeredTo: string | null;
  orderByKey: string;
  order: EOrder;
}

export function useUserParticipantEnrichment({
  participants,
  enrichmentByParticipantId,
  registrationIdsByParticipantId,
  effectiveEditionId,
  editionsById,
  checkinsById,
  checkinsByParticipantId,
  query,
  competitionFilter,
  distanceFilter,
  checkedInFilter,
  registeredFrom,
  registeredTo,
  orderByKey,
  order,
}: UseUserParticipantEnrichmentOptions) {
  return useMemo(() => {
    const enriched: IEnrichedParticipant[] = participants.map(participant => {
      const reg = enrichmentByParticipantId[participant.id];
      const registrationId = registrationIdsByParticipantId[participant.id];
      const edition = effectiveEditionId ? editionsById?.[effectiveEditionId] : undefined;
      const resolvedPrice =
        reg && edition ? getRegistrationDisplayPrice(reg.price, edition, participant) : null;
      const registration: IRegistration | undefined =
        registrationId && reg && effectiveEditionId
          ? {
              id: registrationId,
              participantId: participant.id,
              editionId: effectiveEditionId,
              competitionId: reg.competitionId,
              distanceId: reg.distanceId,
              price: resolvedPrice ?? reg.price ?? 0,
              status: reg.status,
              year: edition?.year ?? 0,
              gender: participant.gender,
              ageGroup: '',
              createdAt: reg.createdAt,
            }
          : undefined;
      const checkinFromStore =
        participant.checkedIn && checkinsById?.[participant.checkedIn]
          ? checkinsById[participant.checkedIn]
          : checkinsByParticipantId[participant.id];

      return {
        ...participant,
        registration,
        checkin: checkinFromStore,
      };
    });

    const filtered = enriched.filter(c => {
      if (!matchesParticipantQuery(query, c)) {
        return false;
      }
      if (!matchesCheckedInFilter(checkedInFilter, isParticipantCheckedIn(c))) {
        return false;
      }
      if (registeredFrom || registeredTo) {
        const atMs = registrationCreatedAtMs(c.registration, c.createdAt);
        if (atMs == null) {
          return false;
        }
        if (!matchesRegistrationDateRange(atMs, registeredFrom, registeredTo)) {
          return false;
        }
      }
      if (!competitionFilter.length && !distanceFilter.length) {
        return true;
      }
      const { competitionId, distanceId } = getParticipantEntryCompetitionDistanceIds(c);
      // Keep owned participants visible so they can register for the filtered event.
      if (!competitionId && !distanceId) {
        return true;
      }
      return matchesCompetitionDistanceFilters(
        competitionFilter,
        distanceFilter,
        competitionId,
        distanceId,
      );
    });

    const edition =
      effectiveEditionId && editionsById?.[effectiveEditionId]
        ? editionsById[effectiveEditionId]
        : null;

    return sortParticipants(filtered, orderByKey, order, {
      editionYear: edition?.year ?? null,
      editionDate: edition?.date,
    });
  }, [
    participants,
    enrichmentByParticipantId,
    registrationIdsByParticipantId,
    effectiveEditionId,
    editionsById,
    checkinsById,
    checkinsByParticipantId,
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    orderByKey,
    order,
  ]);
}
